/* StickyInstallBar — mobile-only sticky bottom bar shown on the Landing
   to make PWA install one-tap. Detects platform:
     • Android/Chrome: uses the deferred `beforeinstallprompt` event.
     • iOS Safari: opens a tutorial sheet with "Aggiungi a Home".
     • Everything else: falls back to "Inizia gratis" (register).
   Auto-hides if the app is already installed (display-mode standalone).
   Dismissible with X, remembers dismissal for 24h via sessionStorage.
*/
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Download, Share, Plus, X, Smartphone } from "lucide-react";
import { useInstall } from "@/context/InstallContext";

const DISMISS_KEY = "orio_install_bar_dismissed_ts";
const DISMISS_TTL_H = 24;

export default function StickyInstallBar() {
  const navigate = useNavigate();
  const { canPrompt, promptInstall, platform, isStandalone, installed } = useInstall();
  const [visible, setVisible] = useState(false);
  const [showIOSTutorial, setShowIOSTutorial] = useState(false);
  const [showAndroidTutorial, setShowAndroidTutorial] = useState(false);

  useEffect(() => {
    if (isStandalone || installed) return;
    // Only on mobile (ios/android). Desktop uses the floating InstallPrompt.
    if (platform !== "android" && platform !== "ios") return;
    try {
      const ts = parseInt(sessionStorage.getItem(DISMISS_KEY) || "0", 10);
      if (ts && Date.now() - ts < DISMISS_TTL_H * 3600_000) return;
    } catch { /* noop */ }
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, [platform, isStandalone, installed]);

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, String(Date.now())); }
    catch { /* noop */ }
    setVisible(false);
  };

  const install = async () => {
    if (platform === "android") {
      // Android → always go to Play Store (ORIO ships as native TWA).
      // promptInstall() in InstallContext handles the redirect.
      await promptInstall();
      setVisible(false);
      return;
    }
    if (platform === "ios") {
      setShowIOSTutorial(true);
      return;
    }
    // Anything else → registration.
    navigate("/register");
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 220 }}
            className="fixed inset-x-0 bottom-0 z-[1400] md:hidden pointer-events-none"
            data-testid="sticky-install-bar"
          >
            <div className="pointer-events-auto mx-3 mb-3 rounded-2xl border"
                 style={{
                   background: "linear-gradient(135deg, #FFFFFF 0%, #FBF3E4 100%)",
                   borderColor: "#F0EBE3",
                   boxShadow: "0 -8px 30px -12px rgba(0,0,0,0.15), 0 4px 12px -6px rgba(255,122,26,0.15)",
                 }}>
              <div className="flex items-center gap-3 p-3">
                <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
                              color: "white",
                              boxShadow: "0 6px 14px -6px rgba(255,122,26,0.55)" }}>
                  <Smartphone className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1A1A1A] leading-tight">
                    {platform === "android" ? "Scarica ORIO su Google Play" : "Installa ORIO gratis"}
                  </div>
                  <div className="text-[10.5px] text-[#7A7A7A] leading-tight mt-0.5">
                    {platform === "ios" ? "Aggiungi a Home in 3 tap" :
                     platform === "android" ? "Google Play · funziona offline" :
                     "Nessuna carta · funziona offline"}
                  </div>
                </div>
                <button
                  onClick={install}
                  data-testid="sticky-install-btn"
                  className="h-10 px-3.5 rounded-full text-[12px] font-semibold text-white inline-flex items-center gap-1.5 shrink-0"
                  style={{
                    background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                    boxShadow: "0 10px 22px -8px rgba(233,69,69,0.50)",
                  }}
                >
                  <Download className="h-3.5 w-3.5" />
                  {platform === "android" ? "Scarica" : "Installa"}
                </button>
                <button
                  onClick={dismiss}
                  data-testid="sticky-install-dismiss"
                  className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F4EFE6]"
                  aria-label="Chiudi">
                  <X className="h-3.5 w-3.5 text-[#7A7A7A]" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* iOS "Add to Home" tutorial sheet */}
      <AnimatePresence>
        {showIOSTutorial && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowIOSTutorial(false)}
              className="fixed inset-0 z-[1490] bg-black/50 backdrop-blur-sm" />
            <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="fixed inset-x-3 bottom-3 z-[1500] rounded-3xl bg-white p-5 border"
              style={{ borderColor: "#F0EBE3", boxShadow: "0 24px 48px -20px rgba(0,0,0,0.35)" }}
              data-testid="ios-install-sheet">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold">
                iPhone · Aggiungi a Home
              </div>
              <div className="font-display text-2xl mt-1 leading-tight">
                Installa ORIO in <span className="orio-grad-text">3 tap</span>
              </div>
              <ol className="mt-5 space-y-3">
                <IOSStep n={1} icon={Share} text="Tocca l'icona Condividi in basso (o in alto) su Safari" />
                <IOSStep n={2} icon={Plus} text={<>Scorri e tocca <b>&quot;Aggiungi a Home&quot;</b></>} />
                <IOSStep n={3} icon={Download} text="Conferma con Aggiungi · ORIO apparirà come app" />
              </ol>
              <button onClick={() => setShowIOSTutorial(false)}
                      className="mt-5 w-full h-11 rounded-full text-[13px] font-semibold text-white"
                      style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)" }}>
                Ho capito
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Android manual tutorial — shown when native prompt is unavailable
          (user already dismissed it in the past, or Chrome hasn't fired the
          event yet). Explains the exact 3 taps in Chrome's menu. */}
      <AnimatePresence>
        {showAndroidTutorial && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowAndroidTutorial(false)}
              className="fixed inset-0 z-[1490] bg-black/50 backdrop-blur-sm" />
            <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="fixed inset-x-3 bottom-3 z-[1500] rounded-3xl bg-white p-5 border"
              style={{ borderColor: "#F0EBE3", boxShadow: "0 24px 48px -20px rgba(0,0,0,0.35)" }}
              data-testid="android-install-sheet">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold">
                Android · Installa app
              </div>
              <div className="font-display text-2xl mt-1 leading-tight">
                Installa ORIO in <span className="orio-grad-text">3 tap</span>
              </div>
              <ol className="mt-5 space-y-3">
                <IOSStep n={1} icon={Plus} text={<>Apri il menù del browser toccando i <b>tre puntini ⋮</b> in alto a destra</>} />
                <IOSStep n={2} icon={Download} text={<>Tocca <b>&quot;Installa app&quot;</b> o <b>&quot;Aggiungi a schermata Home&quot;</b></>} />
                <IOSStep n={3} icon={Share} text="Conferma · ORIO apparirà come una qualsiasi app" />
              </ol>
              <div className="mt-4 rounded-xl border border-[#F0EBE3] bg-[#FAF7F0] p-3 text-[11.5px] text-[#5C5C5C] leading-relaxed">
                <b className="text-[#1A1A1A]">Perché serve il menù?</b> Se hai chiuso l&apos;invito
                in passato, Chrome non lo mostra più per un po&apos;. Con questi 3 tap ORIO
                si installa comunque, con lo stesso risultato.
              </div>
              <button onClick={() => setShowAndroidTutorial(false)}
                      className="mt-5 w-full h-11 rounded-full text-[13px] font-semibold text-white"
                      style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)" }}>
                Ho capito, lo provo
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function IOSStep({ n, icon: Icon, text }) {
  return (
    <li className="flex items-center gap-3">
      <div className="h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-[13px] shrink-0"
           style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)" }}>
        {n}
      </div>
      <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
           style={{ background: "#F4EFE6", color: "#FF7A1A" }}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-[13px] text-[#1A1A1A] leading-snug flex-1">{text}</div>
    </li>
  );
}
